<template>
  <div
    ref="rootRef"
    class="ui-select-next relative w-full min-w-0"
    @keydown="onKeydown"
  >
    <UiFloating
      v-if="isMounted"
      :show="isOpen && !isInert"
      :source="triggerRef"
      :placement="placement"
      :offset-y="6"
      :close-on-click-outside="false"
      class="ui-select-next-content"
      @closed="onClosed"
    >
      <div
        class="flex max-h-[320px] w-full min-w-full flex-col overflow-hidden rounded-lg border border-neutral-400/50 bg-neutral-50 text-sm shadow-lg dark:border-neutral-600 dark:bg-neutral-800"
        :style="contentStyle"
      >
        <div
          v-if="isSearchable"
          class="flex items-center gap-2 border-b border-neutral-400/30 dark:border-neutral-600"
          :class="sizeTokens.search"
        >
          <UiIcon icon="search" :class="sizeTokens.icon" class="shrink-0 text-neutral-400" />
          <input
            ref="searchInput"
            v-model="search"
            type="text"
            aria-autocomplete="list"
            :aria-controls="listboxId"
            :aria-activedescendant="activeOptionId"
            :placeholder="$t('input.search')"
            class="w-full border-0 bg-transparent p-0 text-inherit text-neutral-800 outline-none placeholder:text-neutral-400 dark:text-[#FDFDFD]"
            @click.stop
          >
          <UiIcon
            v-if="search"
            icon="circle-x"
            :class="sizeTokens.icon"
            class="shrink-0 cursor-pointer text-neutral-400 hover:text-neutral-600"
            @click.stop="search = ''"
          />
        </div>

        <div class="flex-1 overflow-auto p-1">
          <slot name="content-start" :search="search" />

          <label
            v-if="multiple && selectAll"
            :class="[itemClasses, sizeTokens.item, 'mb-0.5 font-medium', { [itemDisabledClasses]: isLimitReached }]"
            @mousedown.prevent
          >
            <input
              type="checkbox"
              class="mr-1 size-4 shrink-0 accent-[--c-primary-600]"
              :checked="allSelected"
              :disabled="isLimitReached && !allSelected"
              @change="toggleAll"
            >
            {{ $t('placeholder.select', { field: $t('labels.all').toLowerCase() }) }}
          </label>

          <div v-if="loading" class="flex items-center justify-center gap-2 text-neutral-500" :class="sizeTokens.empty">
            <UiIcon icon="loader" :class="sizeTokens.icon" class="animate-spin" />
            {{ $t('feedback.loading') }}
          </div>

          <ul
            v-else-if="visibleItems.length"
            :id="listboxId"
            role="listbox"
            :aria-multiselectable="multiple"
          >
            <template v-for="(item, i) in visibleItems" :key="item.type === 'separator' ? `sep-${i}` : `${item.value}-${item.index}`">
              <li
                v-if="item.type === 'separator'"
                role="separator"
                :class="separatorClasses"
              />

              <li
                v-else-if="item.type === 'label'"
                role="presentation"
                class="px-2 pt-1.5 pb-0.5 text-xs font-semibold uppercase text-neutral-400 dark:text-neutral-500"
              >
                {{ item.label }}
              </li>

              <li
                v-else
                :id="optionId(item)"
                role="option"
                :aria-selected="isSelected(item.value)"
                :aria-disabled="item.disabled"
                :class="[
                  itemClasses,
                  sizeTokens.item,
                  {
                    [itemDisabledClasses]: item.disabled,
                    [itemActiveClasses]: activeIndex === i && !item.disabled,
                    'font-medium text-[--c-primary-700] dark:text-white': isSelected(item.value)
                  }
                ]"
                @mousedown.prevent
                @mousemove="!item.disabled && setActive(i)"
                @click.stop="onItemClick(item)"
              >
                <span
                  v-if="multiple"
                  :class="[
                    'flex shrink-0 items-center justify-center rounded border transition-colors',
                    isSelected(item.value)
                      ? 'border-[--c-primary-600] bg-[--c-primary-600] text-white'
                      : 'border-neutral-400 dark:border-neutral-500',
                    sizeTokens.itemIcon
                  ]"
                  aria-hidden="true"
                >
                  <UiIcon v-if="isSelected(item.value)" icon="check" size="12" stroke-width="3" />
                </span>

                <slot
                  name="item"
                  :item="item"
                  :selected="isSelected(item.value)"
                  :active="activeIndex === i"
                >
                  <slot name="item-leading" :item="item" />
                  <UiIcon v-if="item.icon" :icon="item.icon" :class="sizeTokens.itemIcon" class="shrink-0 text-neutral-400" />
                  <span class="flex min-w-0 flex-1 flex-col">
                    <slot name="item-label" :item="item">
                      <span class="truncate">{{ item.label }}</span>
                    </slot>
                    <span v-if="item.description" :class="itemDescriptionClasses">
                      <slot name="item-description" :item="item">{{ item.description }}</slot>
                    </span>
                  </span>
                </slot>

                <span class="ms-auto flex shrink-0 items-center gap-1">
                  <UiIcon
                    v-if="!multiple && isSelected(item.value)"
                    :icon="selectedIcon"
                    :class="sizeTokens.icon"
                    class="text-[--c-primary-600]"
                  />
                  <button
                    v-if="editable"
                    type="button"
                    class="rounded p-0.5 text-neutral-400 transition-colors hover:bg-neutral-300/60 hover:text-neutral-700 dark:hover:bg-neutral-600 dark:hover:text-white"
                    :aria-label="$t('button.edit')"
                    @click.stop="emit('edit', item)"
                  >
                    <UiIcon icon="pencil" :class="sizeTokens.icon" />
                  </button>
                  <button
                    v-if="removable"
                    type="button"
                    class="rounded p-0.5 text-neutral-400 transition-colors hover:bg-red-100 hover:text-red-500 dark:hover:bg-red-900/40 dark:hover:text-red-300"
                    :aria-label="$t('labels.remove')"
                    @click.stop="emit('remove', item)"
                  >
                    <UiIcon icon="trash" :class="sizeTokens.icon" />
                  </button>
                </span>
              </li>
            </template>
          </ul>

          <div v-else class="text-center text-neutral-500" :class="sizeTokens.empty">
            <slot name="no-result" :search="search">
              {{ $t('feedback.noFound') }}
            </slot>
          </div>

          <slot name="content-end" :search="search" />
        </div>

        <div
          v-if="isLimitReached"
          class="border-t border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-700 dark:border-amber-900 dark:bg-amber-900/30 dark:text-amber-200"
        >
          {{ $t('feedback.limitReached', { limit }) }}
        </div>
      </div>
    </UiFloating>

    <div
      :id="id || undefined"
      ref="triggerRef"
      class="ui-select-next-trigger"
      :class="[triggerBase, sizeTokens.trigger, stateClass]"
      :style="customBorderColor ? { borderColor: customBorderColor } : undefined"
      role="combobox"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
      :aria-controls="isOpen ? listboxId : undefined"
      :aria-activedescendant="activeOptionId"
      :aria-disabled="isInert"
      :aria-required="required"
      :tabindex="isInert ? -1 : 0"
      @click="toggleOpen"
    >
      <UiIcon v-if="icon" :icon="icon" :class="sizeTokens.icon" class="shrink-0 text-neutral-400" />

      <slot
        name="selector"
        :open="isOpen"
        :selected-items="selectedItems"
        :selected-values="selectedValues"
        :is-selected="selectedValues.length > 0"
      >
        <template v-if="multiple">
          <div v-if="!selectedItems.length" class="flex-1 truncate text-[#BBBBBB] dark:text-neutral-500">
            {{ placeholder || '' }}
          </div>
          <div v-else class="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
            <span
              v-for="item in displayedTags"
              :key="String(item.value)"
              :class="['flex min-w-0 max-w-full items-center whitespace-nowrap bg-[--c-primary-400] font-semibold text-[--c-primary-800] dark:bg-[--c-primary-700] dark:text-white', sizeTokens.tag]"
            >
              <span class="truncate">{{ item.label }}</span>
              <button
                v-if="!isInert"
                type="button"
                class="shrink-0 opacity-60 transition-opacity hover:opacity-100"
                :aria-label="$t('labels.remove')"
                @click.stop="removeValue(item.value)"
              >
                <UiIcon icon="x" :class="sizeTokens.tagRemove" size="16" />
              </button>
            </span>
            <span
              v-if="hiddenCount"
              :class="['flex shrink-0 items-center justify-center rounded-full bg-[--c-primary-200] font-semibold text-[--c-primary-700] dark:bg-[--c-primary-700] dark:text-white', sizeTokens.counter]"
            >
              +{{ hiddenCount }}
            </span>
          </div>
        </template>
        <div v-else class="flex min-w-0 flex-1 items-center">
          <span v-if="selectedValues.length" class="truncate">{{ singleLabel }}</span>
          <span v-else class="truncate text-[#BBBBBB] dark:text-neutral-500">{{ placeholder || '' }}</span>
        </div>
      </slot>

      <span class="ms-auto flex shrink-0 items-center gap-1.5">
        <UiIcon v-if="loading" icon="loader" :class="sizeTokens.icon" class="animate-spin text-neutral-400" />
        <UiIcon v-else-if="blocked || state === 'blocked'" icon="lock" :class="sizeTokens.icon" class="text-neutral-400" />
        <button
          v-else-if="clearable && selectedValues.length && !isInert"
          type="button"
          class="text-[#FF4980] transition-opacity hover:opacity-70"
          :aria-label="$t('button.clear', { text: '' })"
          @click.stop="clearSelection"
        >
          <UiIcon icon="circle-x" :class="sizeTokens.icon" />
        </button>
        <UiIcon
          v-if="!loading"
          :icon="trailingIcon"
          :class="sizeTokens.icon"
          class="text-neutral-400 transition-transform duration-200"
          :style="{ transform: isOpen ? 'rotate(180deg)' : undefined }"
        />
      </span>
    </div>

    <input
      v-for="value in formValues"
      :key="`${value}`"
      type="hidden"
      :name="name"
      :value="value"
    >
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { onClickOutside } from '@vueuse/core'

import type { Placement } from '@floating-ui/vue'

import UiFloating from './util/floating.vue'
import UiIcon from './icon.vue'
import { useSelect } from './util/useSelect'
import {
  itemActiveClasses,
  itemClasses,
  itemDescriptionClasses,
  itemDisabledClasses,
  separatorClasses,
  sizeClasses,
  stateClasses,
  triggerBase
} from './util/select.theme'
import type { SelectItem, SelectRawItem, SelectSearchable, SelectSize, SelectState, SelectValue } from './util/select.types'

defineOptions({ name: 'UiSelectNext', inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    items?: SelectRawItem[]
    multiple?: boolean
    placeholder?: string
    size?: SelectSize
    state?: SelectState
    disabled?: boolean
    blocked?: boolean
    clearable?: boolean
    searchable?: SelectSearchable
    searchThreshold?: number
    searchableFields?: string[]
    selectedOnTop?: boolean
    closeOnClick?: boolean
    closeOnOutside?: boolean
    placement?: Placement
    limit?: number
    loading?: boolean
    selectedIcon?: string
    trailingIcon?: string
    icon?: string
    maxDisplay?: number
    valueKey?: string
    labelKey?: string
    descriptionKey?: string
    customBorderColor?: string
    id?: string
    name?: string
    required?: boolean
    editable?: boolean
    removable?: boolean
    selectAll?: boolean
  }>(),
  {
    items: () => [],
    multiple: false,
    size: 'md',
    state: 'default',
    disabled: false,
    blocked: false,
    clearable: false,
    searchable: 'auto',
    searchThreshold: 8,
    closeOnClick: undefined,
    selectedOnTop: true,
    closeOnOutside: true,
    placement: 'bottom-start',
    loading: false,
    selectedIcon: 'check',
    trailingIcon: 'chevron-down',
    maxDisplay: 3,
    valueKey: 'value',
    labelKey: 'label',
    descriptionKey: '',
    required: false,
    editable: false,
    removable: false,
    selectAll: false
  }
)

const emit = defineEmits<{
  change: [value: SelectValue | SelectValue[] | undefined]
  select: [item: SelectItem]
  clear: []
  'limit-reached': [limit: number]
  edit: [item: SelectItem]
  remove: [item: SelectItem]
  search: [query: string]
}>()

const model = defineModel<SelectValue | SelectValue[] | undefined>({ default: undefined })
const isOpen = defineModel<boolean>('open', { default: false })

const uid = Math.random().toString(36).slice(2, 8)
const listboxId = `ui-select-listbox-${uid}`
const optionId = (item: { index: number }) => `ui-select-option-${uid}-${item.index}`

const rootRef = ref<HTMLElement>()
const triggerRef = ref<HTMLElement>()
const searchInput = ref<HTMLInputElement>()

const isMounted = ref(false)
onMounted(() => { isMounted.value = true })

const {
  search,
  selectedValues,
  selectedItems,
  visibleItems,
  isSearchable,
  isLimitReached,
  allSelected,
  activeIndex,
  activeItem,
  isSelectable,
  isSelected,
  moveActive,
  setActive,
  setActiveByValue,
  typeAhead,
  toggleItem,
  toggleAll,
  clear,
  removeValue
} = useSelect({
  items: () => props.items,
  model,
  multiple: () => props.multiple,
  searchable: () => props.searchable,
  searchThreshold: () => props.searchThreshold,
  searchableFields: () => props.searchableFields,
  selectedOnTop: () => props.selectedOnTop,
  limit: () => props.limit,
  valueKey: () => props.valueKey,
  labelKey: () => props.labelKey,
  descriptionKey: () => props.descriptionKey
})

const sizeTokens = computed(() => sizeClasses[props.size])

const isInert = computed(() => props.disabled || props.blocked || props.state === 'blocked')

const stateClass = computed(() => {
  if (props.disabled) return stateClasses.disabled
  if (props.blocked || props.state === 'blocked') return stateClasses.blocked
  return stateClasses[props.state]
})

const shouldCloseOnClick = computed(() => props.closeOnClick ?? !props.multiple)

const displayedTags = computed(() => selectedItems.value.slice(0, props.maxDisplay))
const hiddenCount = computed(() => Math.max(0, selectedItems.value.length - props.maxDisplay))
const singleLabel = computed(() => selectedItems.value[0]?.label ?? '')

const formValues = computed(() => (props.name ? selectedValues.value : []))

const activeOptionId = computed(() => (isOpen.value && activeItem.value ? optionId(activeItem.value) : undefined))

const contentStyle = computed(() =>
  triggerRef.value ? { width: `${triggerRef.value.getBoundingClientRect().width}px` } : undefined
)

const scrollActiveIntoView = () => {
  nextTick(() => {
    const item = visibleItems.value[activeIndex.value]
    if (!item) return
    document.getElementById(optionId(item))?.scrollIntoView({ block: 'nearest' })
  })
}

const openMenu = () => {
  if (isInert.value) return
  isOpen.value = true
}

const closeMenu = () => {
  isOpen.value = false
}

const toggleOpen = () => {
  if (isInert.value) return
  if (isOpen.value) closeMenu()
  else openMenu()
}

const onItemClick = (item: SelectItem) => {
  if (isInert.value || item.disabled) return
  const adding = props.multiple && !isSelected(item.value)
  if (adding && props.limit != null && selectedValues.value.length >= props.limit) {
    emit('limit-reached', props.limit)
    return
  }
  const changed = toggleItem(item)
  if (!changed) return
  emit('select', changed)
  if (shouldCloseOnClick.value) closeMenu()
}

const clearSelection = () => {
  clear()
  emit('clear')
}

const firstSelectableIndex = () => visibleItems.value.findIndex((item) => isSelectable(item))

const lastSelectableIndex = () => {
  for (let i = visibleItems.value.length - 1; i >= 0; i -= 1) {
    if (isSelectable(visibleItems.value[i])) return i
  }
  return -1
}

const onKeydown = (event: KeyboardEvent) => {
  if (isInert.value) return
  const key = event.key

  switch (key) {
    case 'ArrowDown':
      event.preventDefault()
      if (!isOpen.value) openMenu()
      else { moveActive(1); scrollActiveIntoView() }
      break
    case 'ArrowUp':
      event.preventDefault()
      if (!isOpen.value) openMenu()
      else { moveActive(-1); scrollActiveIntoView() }
      break
    case 'Home':
      if (isOpen.value) { event.preventDefault(); setActive(firstSelectableIndex()) }
      break
    case 'End':
      if (isOpen.value) { event.preventDefault(); setActive(lastSelectableIndex()) }
      break
    case 'Enter':
      if (!isOpen.value) { event.preventDefault(); openMenu(); break }
      if (activeItem.value && !activeItem.value.disabled) { event.preventDefault(); onItemClick(activeItem.value) }
      break
    case ' ':
      if (isOpen.value && !search.value) {
        event.preventDefault()
        if (activeItem.value && !activeItem.value.disabled) onItemClick(activeItem.value)
      } else if (!isSearchable.value) {
        event.preventDefault()
        if (!isOpen.value) openMenu()
      }
      break
    case 'Escape':
      if (isOpen.value) { event.preventDefault(); closeMenu() }
      break
    case 'Tab':
      if (isOpen.value) closeMenu()
      break
    case 'Backspace':
      if (props.multiple && !search.value && selectedValues.value.length) {
        event.preventDefault()
        removeValue(selectedValues.value[selectedValues.value.length - 1])
      }
      break
    default:
      if (!isSearchable.value && key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
        typeAhead(key)
        scrollActiveIntoView()
      }
  }
}

const onClosed = () => {
  isOpen.value = false
}

watch(isOpen, (open) => {
  if (open) {
    nextTick(() => {
      if (isSearchable.value) searchInput.value?.focus()
      if (selectedValues.value.length) setActiveByValue(selectedValues.value[0])
      if (activeIndex.value < 0) setActive(firstSelectableIndex())
      scrollActiveIntoView()
    })
  } else {
    search.value = ''
    activeIndex.value = -1
  }
})

watch(search, (query) => emit('search', query))

watch(model, (value) => emit('change', value))

onClickOutside(rootRef, () => {
  if (!props.closeOnOutside || !isOpen.value) return
  closeMenu()
})

defineExpose({
  open: openMenu,
  close: closeMenu,
  toggle: toggleOpen,
  isOpen: computed(() => isOpen.value)
})
</script>
